import { useCenterStore } from '@caradasstore/CenterStore'
import { useLoadingStore } from '@caradasstore/LoadingStore'
import { useMobileToolbarStore } from '@caradasstore/MobileToolbarStore'
import { ItemPerPageSelector, SearchOrAddData } from '@components/common'
import { AdasBox, AdasPagination, AdasPaper, AdasStack, AdasTable } from '@components/wrapper-components'
import { Building2, Download, Plus, Search, X } from 'lucide-react'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useHistory } from 'react-router-dom'
import { getCalibrationCenter, getCalibrationCenterList } from '../../../api/api'
import { DATE_FORMATS, MANAGE_LOCATIONS_HEADERS, TABLE_HEADER_STYLE } from '../../../constants'
import { scrollPageTop } from '../../../utils/useScrollTop'
import { ccidFormatter, downloadAsFile, formatDate, roundToGreatestInteger } from '../../../utils/utils'
import { Logger } from '../../../logger'

export const CalibrationCenterListPage = () => {
  const [searchInput, setSearchInput] = useState('')
  const [calibrationCenters, setCalibrationCenters] = useState([])
  const [pageNo, setPageNo] = useState(0)
  const [totalCount, setTotalCount] = useState(0)
  const [pageSize, setPageSize] = useState(200)
  const [sort, setSort] = useState('ASC')
  const [sortField, setSortField] = useState('name')
  const setCenterDetail = useCenterStore((store) => store.setCenterDetail)
  const upsertCenterSummaries = useCenterStore((store) => store.upsertCenterSummaries)
  const setLoading = useLoadingStore((store) => store.setLoading)
  const [isActive, setIsActive] = useState(true) // true = show only active by default
  const searchOpen = useMobileToolbarStore((s) => s.searchOpen)
  const closeMobileToolbar = useMobileToolbarStore((s) => s.closeAll)
  useEffect(() => () => closeMobileToolbar(), [closeMobileToolbar])

  const paginationHandler = (_event, page) => {
    setPageNo(page - 1)
    scrollPageTop()
  }

  const pageSizeHandler = (event) => {
    setPageNo(0)
    setPageSize(event.target.value)
    scrollPageTop()
  }

  useEffect(() => {
    setLoading(true)

    // CAR-3522: this page filters / sorts / paginates client-side, so it needs
    // the full list in memory. Previously we capped at pageSize=500, which
    // silently dropped every center past position 500 (e.g., Business AI
    // Validations / CC-0078 was missing while still visible in Global Directory).
    // First call returns totalCount; if there are more rows than we just got,
    // refetch with pageSize = totalCount so the full dataset is loaded.
    const PROBE_PAGE_SIZE = 500

    const fetchData = async () => {
      try {
        const initialResponse = await getCalibrationCenterList({
          pageNo: 0,
          pageSize: PROBE_PAGE_SIZE
        })

        if (initialResponse?.status !== 200) {
          return
        }

        const total = initialResponse.data.totalCount || 0
        let allCenters = initialResponse.data.calibrationCenters || []

        if (total > allCenters.length) {
          const fullResponse = await getCalibrationCenterList({
            pageNo: 0,
            pageSize: total
          })
          if (fullResponse?.status === 200) {
            allCenters = fullResponse.data.calibrationCenters || []
          }
        }

        setCalibrationCenters(allCenters)
        setTotalCount(total)
        // CAR-3630: share the fetched list with the navbar location switcher so it
        // shows every location, not just the current one.
        upsertCenterSummaries(allCenters)
        scrollPageTop()
      } catch (error) {
        Logger.error({ message: error, payload: { file: 'CalibrationCenterListPage', method: 'getCalibrationCenterList' } })
      } finally {
        setLoading(false)
      }
    }

    fetchData()
  }, [])

  const searchInputHandler = (e) => {
    setSearchInput(e.target.value)
    setPageNo(0)
  }

  const clickSortHandler = useCallback(
    (field) => {
      if (sortField === field) {
        setSort(sort === 'ASC' ? 'DESC' : 'ASC')
      } else {
        setSort('ASC')
        setSortField(field)
      }
    },
    [sort, sortField]
  )

  const handleCCSwitch = async (data) => {
    const ccResponse = await getCalibrationCenter({
      ccid: data.id
    })
    if (ccResponse.status === 200) {
      setCenterDetail(ccResponse.data)
      history.push('/')
    }
  }

  function getValueByPath(obj, sortField) {
    const value = obj[sortField]

    if (sortField === 'owner') {
      return (value?.owners?.[0]?.name ?? '') + ''
    }
    if (typeof value === 'number') {
      return value
    }
    return value ?? ''
  }

  const sortedCalibrationCenterList = useMemo(() => {
    let filteredSortedList = calibrationCenters

    // Apply active/all filter
    if (isActive !== '') {
      filteredSortedList = filteredSortedList.filter((cc) => cc.is_active === true)
    }

    // Apply search filter
    if (searchInput) {
      const search = searchInput.toLowerCase()
      filteredSortedList = filteredSortedList.filter(
        (cc) =>
          cc.name?.toLowerCase().includes(search) ||
          `cc-${String(cc.id).padStart(4, '0')}`.includes(search) ||
          String(cc.group_number)?.includes(search) ||
          cc.group_name?.toLowerCase().includes(search) ||
          cc.support_lead?.toLowerCase().includes(search) ||
          cc.owners[0]?.name?.toLowerCase().includes(search) ||
          cc.pbi_account_id?.toLowerCase().includes(search) ||
          new Date(cc.opening_date).toLocaleDateString().includes(search) ||
          cc.pbi_account_id?.toLowerCase().includes(search) ||
          (cc.is_active ? 'active' : 'inactive').startsWith(search)
      )
    }
    const filteredCount = filteredSortedList.length
    // Sorting
    filteredSortedList = filteredSortedList.sort((a, b) => {
      const valueA = getValueByPath(a, sortField)
      const valueB = getValueByPath(b, sortField)
      const typeA = typeof valueA
      const typeB = typeof valueB

      if (typeA !== typeB) {
        return String(valueA).localeCompare(String(valueB), 'en', { sensitivity: 'base' }) * (sort === 'ASC' ? 1 : -1)
      }

      if (typeA === 'string') {
        return valueA.localeCompare(valueB, 'en', { sensitivity: 'base' }) * (sort === 'ASC' ? 1 : -1)
      }

      return (valueA < valueB ? -1 : valueA > valueB ? 1 : 0) * (sort === 'ASC' ? 1 : -1)
    })

    return {
      data: filteredSortedList.slice(pageNo * pageSize, pageNo * pageSize + pageSize),
      total: filteredCount
    }
  }, [calibrationCenters, searchInput, sort, sortField, pageNo, pageSize, isActive])

  const downloadCCList = useCallback(async () => {
    const {
      data: { calibrationCenters: ccList }
    } = await getCalibrationCenterList({
      pageNo: 0,
      pageSize: totalCount
    })

    let filtered = ccList
    if (isActive !== '') {
      filtered = ccList.filter((cc) => cc.is_active === true)
    }

    let ccExport = filtered.map((cc, index) => [
      index + 1,
      '"' + cc.name + '"',
      ccidFormatter(cc.id),
      cc.group_number,
      cc.group_name,
      cc.owners[0]?.name,
      cc.support_lead,
      cc.pbi_account_id,
      formatDate(cc.opening_date, DATE_FORMATS.ISO_DATE_STRING),
      cc.is_active ? 'Active' : 'Inactive',
      '"' + cc.shop_address + '"',
      cc.shop_city,
      cc.shop_state,
      cc.shop_zip,
      `${cc.phone}`,
      cc.is_scheduler_enabled ? 'Active' : 'Inactive',
      cc.is_pgw_enabled ? 'Active' : 'Inactive',
      cc.is_sf_enabled ? 'Active\n' : 'Inactive\n'
    ])

    ccExport = [
      [
        'Index',
        'CC Name',
        'CC #',
        'Group #',
        'Group Name',
        'Owner',
        'Support Lead',
        'PBI Account',
        'Open Date',
        'Status',
        'Address',
        'City',
        'State',
        'Zip',
        'Phone',
        'Calendar',
        'PGW',
        'State Farm\n'
      ],
      ...ccExport
    ]

    const fileName = isActive ? 'active_calibration_center_list.csv' : 'all_calibration_center_list.csv'
    const value = new Blob(ccExport, { type: 'text/plain' })
    downloadAsFile(value, fileName)
  }, [totalCount, isActive])

  const history = useHistory()
  const handleStatusToggle = (status) => {
    setIsActive(status)
    setPageNo(0)
  }
  // Reset pageNo to 0 when search is cleared
  useEffect(() => {
    if (searchInput === '') {
      setPageNo(0)
    }
  }, [searchInput])

  // Ensure pageNo is within valid range when filtered data changes
  useEffect(() => {
    const maxPageNo = Math.floor(sortedCalibrationCenterList.total / pageSize)
    if (pageNo > maxPageNo) {
      setPageNo(0)
    }
  }, [sortedCalibrationCenterList.total, pageSize, pageNo])

  const allCount = calibrationCenters.length
  const activeCount = useMemo(() => calibrationCenters.filter((cc) => cc.is_active === true).length, [calibrationCenters])
  const statusTabs = [
    { value: '', label: 'All', count: allCount },
    { value: true, label: 'Active', count: activeCount }
  ]

  return (
    <div className='mx-auto max-w-[1400px] px-3 py-6 min-[435px]:px-0'>
      {/* Page heading (desktop only) */}
      <AdasBox className='hidden min-[435px]:flex pt-6 md:pt-4 mb-6 flex-wrap items-center justify-between gap-3'>
        <h1 className='text-[32px] font-bold leading-tight tracking-tight text-foreground'>Manage Locations</h1>
      </AdasBox>

      {/* Mobile inline search */}
      {searchOpen && (
        <div className='min-[435px]:hidden mb-3 flex items-center gap-2 rounded-lg bg-[#F4F6F9] px-3 py-2.5'>
          <Search className='h-4 w-4 text-[#6B7280]' />
          <input
            type='text'
            value={searchInput}
            onChange={(e) => searchInputHandler(e)}
            autoFocus
            placeholder='Search locations'
            className='flex-1 border-0 bg-transparent text-[14px] text-[#0A1628] placeholder:text-[#6B7280] focus:outline-none'
          />
          {searchInput && (
            <button
              type='button'
              onClick={() => searchInputHandler({ target: { value: '' } })}
              aria-label='Clear search'
              className='inline-flex h-6 w-6 items-center justify-center rounded-full bg-white text-[#475569]'
            >
              <X className='h-3 w-3' />
            </button>
          )}
        </div>
      )}

      <div className='mt-2 flex items-center gap-6 border-b border-[#EBEDF0]'>
        {statusTabs.map((t) => {
          const active = isActive === t.value
          return (
            <button
              key={String(t.value)}
              type='button'
              onClick={() => handleStatusToggle(t.value)}
              className={`pb-3 inline-flex items-center gap-1.5 text-[14px] font-medium transition-colors ${
                active ? 'text-[#1F6FEB] border-b-2 border-[#1F6FEB]' : 'text-[#6B7280] hover:text-[#334155]'
              }`}
            >
              {t.label}
              <span
                className={`inline-flex h-5 min-w-5 items-center justify-center rounded-full px-1.5 text-[11px] font-bold ${
                  active ? 'bg-[#E8EFFF] text-[#1F6FEB]' : 'bg-[#EBEDF0] text-[#6B7280]'
                }`}
              >
                {t.count}
              </span>
            </button>
          )
        })}
      </div>

      {/* Desktop search bar + action buttons */}
      <AdasBox className='hidden min-[435px]:flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mt-5'>
        <AdasBox className='flex items-center flex-1 sm:flex-[0.6]'>
          <SearchOrAddData
            variant='withAddButton'
            searchInput={searchInput}
            searchInputHandler={searchInputHandler}
            onAddClick={() => history.push('/manage-location/new-location')}
          />
        </AdasBox>
        <AdasBox className='flex items-center gap-3 shrink-0'>
          <button
            onClick={downloadCCList}
            className='inline-flex items-center gap-2 h-10 px-4 bg-white border border-[#D1D5D8] rounded-lg text-[13px] font-semibold text-[#0A1628] hover:bg-[#F8FAFC] transition-colors'
          >
            <Download className='h-4 w-4' />
            Export CSV
          </button>
        </AdasBox>
      </AdasBox>

      {/* Desktop table */}
      <div className='hidden min-[435px]:block bg-card rounded-card border border-border shadow-level-1 overflow-hidden mt-5'>
        {calibrationCenters.length === 0 ? (
          <div className='py-10 text-center text-[13px] text-[#6B7280]'>No locations found.</div>
        ) : (
          <AdasTable
            containerProps={{ component: AdasPaper, elevation: 0, className: 'shadow-none' }}
            tableProps={{
              sx: {
                '.MuiTableCell-root': { border: 'none', padding: '2px 10px' },
                overflowX: 'auto'
              },
              'aria-label': 'location table'
            }}
            columns={MANAGE_LOCATIONS_HEADERS}
            headerStyle={TABLE_HEADER_STYLE}
            sortField={sortField}
            sortDirection={sort}
            onSort={clickSortHandler}
            data={sortedCalibrationCenterList?.data}
            onSelect={handleCCSwitch}
          />
        )}
      </div>

      {/* Mobile card list */}
      <AdasBox className='min-[435px]:hidden mt-4 space-y-3 pb-24'>
        {sortedCalibrationCenterList.data.length === 0 ? (
          <div className='rounded-xl border border-[#D1D5D8] bg-white px-4 py-10 text-center text-[14px] text-[#6B7280]'>
            {searchInput ? 'No locations match your search.' : 'No locations yet.'}
          </div>
        ) : (
          sortedCalibrationCenterList.data.map((cc) => {
            const owner = cc.owners?.[0]?.name
            const openDate = cc.opening_date ? formatDate(cc.opening_date, DATE_FORMATS.ISO_DATE_STRING) : null
            return (
              <button
                key={cc.id}
                type='button'
                onClick={() => handleCCSwitch(cc)}
                className='w-full rounded-xl border border-[#D1D5D8] bg-white p-4 text-left transition-colors hover:bg-[#F8FAFC]'
              >
                <div className='flex items-start justify-between gap-3'>
                  <div className='flex min-w-0 flex-1 items-start gap-3'>
                    <div className='flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-lg bg-[#E5EBF5]'>
                      <Building2 className='h-5 w-5 text-[#1F6FEB]' />
                    </div>
                    <div className='min-w-0 flex-1'>
                      <div className='text-[15px] font-semibold leading-tight text-[#1F6FEB]'>{cc.name}</div>
                      <div className='mt-1 font-mono text-[12px] text-[#475569]'>{ccidFormatter(cc.id)}</div>
                    </div>
                  </div>
                  <span
                    className={`inline-flex shrink-0 items-center rounded-full px-2.5 py-0.5 text-[11px] font-semibold ${
                      cc.is_active ? 'bg-[#DCFCE7] text-[#16803F]' : 'bg-[#EBEDF0] text-[#6B7280]'
                    }`}
                  >
                    {cc.is_active ? 'Active' : 'Inactive'}
                  </span>
                </div>
                <div className='mt-3 grid grid-cols-3 gap-3 border-t border-[#EBEDF0] pt-3 text-[12px]'>
                  <div>
                    <div className='text-[10px] font-semibold uppercase tracking-wide text-[#6B7280]'>Group #</div>
                    <div className='mt-0.5 text-[13px] font-semibold text-[#0A1628]'>{cc.group_number || '—'}</div>
                  </div>
                  <div>
                    <div className='text-[10px] font-semibold uppercase tracking-wide text-[#6B7280]'>Owner</div>
                    <div className='mt-0.5 truncate text-[13px] font-semibold text-[#0A1628]'>{owner || '—'}</div>
                  </div>
                  <div>
                    <div className='text-[10px] font-semibold uppercase tracking-wide text-[#6B7280]'>Open Date</div>
                    <div className='mt-0.5 font-mono text-[12px] font-semibold text-[#0A1628]'>{openDate || '—'}</div>
                  </div>
                </div>
              </button>
            )
          })
        )}
      </AdasBox>

      {/* Floating Add Location button (mobile only) */}
      <button
        type='button'
        onClick={() => history.push('/manage-location/new-location')}
        className='min-[435px]:hidden fixed bottom-6 right-4 z-30 inline-flex items-center gap-2 rounded-full bg-[#0A1628] px-6 py-3.5 text-[14px] font-bold text-white shadow-lg transition-colors hover:bg-[#1F2D3D]'
      >
        <Plus className='h-5 w-5' />
        Add Location
      </button>

      <AdasPaper elevation={0} className='hidden min-[435px]:flex items-center justify-between p-[20px] mt-[20px]'>
        <AdasBox className='w-[20%]'>
          <ItemPerPageSelector pageSizeHandler={pageSizeHandler} pageSize={pageSize} />
        </AdasBox>
        <AdasBox>
          <AdasStack spacing={2}>
            <AdasPagination
              page={pageNo + 1}
              onChange={paginationHandler}
              count={roundToGreatestInteger(sortedCalibrationCenterList.total / pageSize)}
              shape='rounded'
            />
          </AdasStack>
        </AdasBox>
      </AdasPaper>
    </div>
  )
}
